/**
 * Public access-key URLs are bearer credentials.
 *
 * The attestation link, the lodgement summary and the insurer invoice view all resolve a
 * record by an unguessable key carried in the URL, and invite and password-reset links carry their secrets as
 * a query parameter. Any telemetry that records the page URL — PostHog's `$current_url` /
 * `$pathname` / `$referrer`, Sentry's `request.url`, navigation breadcrumbs, span data — would
 * otherwise ship those credentials to a third party. These helpers strip the secret segment
 * and leave the route shape intact, so the telemetry stays useful.
 *
 * Every pattern here is linear (no nested or overlapping quantifiers); keep it that way.
 */

export const REDACTED_ACCESS_KEY = '[redacted]';

/**
 * Path shapes whose last segment is an access key. The captured prefix is preserved. A segment
 * ends at a path, query or fragment delimiter, and also at whitespace or a quote so the
 * patterns hold inside free text (breadcrumb messages, exception values, JSON fragments).
 */
const ACCESS_KEY_PATH_PATTERNS: readonly RegExp[] = [
    /(\/public\/lodgement-summary\/)[^/?#\s"'<>]+/i,
    /(\/public\/[^/?#\s"'<>]+\/invoice\/view\/)[^/?#\s"'<>]+/i,
    /(\/attestation\/)[^/?#\s"'<>]+/i,
];

/** Query parameters that carry a secret. The captured `?name=` prefix is preserved. */
const SECRET_QUERY_PARAM_PATTERN = /([?&](?:accessKey|inviteSecret|resetSecret)=)[^&#\s"'<>]*/i;

const GLOBAL_PATH_PATTERNS = ACCESS_KEY_PATH_PATTERNS.map((pattern) => new RegExp(pattern.source, 'gi'));
const GLOBAL_QUERY_PATTERN = new RegExp(SECRET_QUERY_PARAM_PATTERN.source, 'gi');

/** Whether a pathname addresses a record by an access key (e.g. to skip session recording). */
export function isAccessKeyPath(pathname: string): boolean {
    return ACCESS_KEY_PATH_PATTERNS.some((pattern) => pattern.test(pathname));
}

/**
 * Whether a location carries a secret in its path or its query string — the recording gate for
 * Sentry Replay and PostHog session recording. `search` is the raw `?a=b` string (with or without
 * the leading `?`); the query pattern anchors on the `?`/`&` delimiter, so a bare string is given one.
 */
export function hasSecretInLocation(pathname: string, search: string): boolean {
    if (isAccessKeyPath(pathname)) {
        return true;
    }
    if (search.length === 0) {
        return false;
    }
    const delimited = search.startsWith('?') || search.startsWith('&') ? search : `?${search}`;
    return SECRET_QUERY_PARAM_PATTERN.test(delimited);
}

/** Redact every access-key path segment and secret query parameter in a string. */
export function redactAccessKeys(text: string): string {
    let result = text;
    for (const pattern of GLOBAL_PATH_PATTERNS) {
        result = result.replace(pattern, `$1${REDACTED_ACCESS_KEY}`);
    }
    return result.replace(GLOBAL_QUERY_PATTERN, `$1${REDACTED_ACCESS_KEY}`);
}

const MAX_DEPTH = 8;

function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}

/**
 * Redact access keys in every string reachable from `value` through plain objects and arrays.
 * Other values (numbers, dates, class instances, functions) are returned unchanged. Returns
 * the same reference when nothing needed redacting, so untouched telemetry is not re-allocated.
 */
export function redactAccessKeysDeep<T>(value: T, depth = 0): T {
    if (typeof value === 'string') {
        return redactAccessKeys(value) as T;
    }
    if (depth >= MAX_DEPTH) {
        // Nothing below the cap is inspected, so nothing below it may leave either.
        return (Array.isArray(value) || isPlainObject(value) ? '[redacted: depth]' : value) as T;
    }
    if (Array.isArray(value)) {
        let changed = false;
        const next = value.map((item) => {
            const redacted = redactAccessKeysDeep(item, depth + 1);
            changed = changed || redacted !== item;
            return redacted;
        });
        return (changed ? next : value) as T;
    }
    if (isPlainObject(value)) {
        let changed = false;
        const next: Record<string, unknown> = {};
        for (const [key, item] of Object.entries(value)) {
            const redacted = redactAccessKeysDeep(item, depth + 1);
            changed = changed || redacted !== item;
            Object.defineProperty(next, key, { value: redacted, enumerable: true, writable: true, configurable: true });
        }
        return (changed ? next : value) as T;
    }
    return value;
}
